import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Lightning, ArrowRight, WarningCircle, CheckCircle,
  Microphone, SpeakerHigh, Brain, Target, ShieldCheck
} from "@phosphor-icons/react";

const STAGE_COLORS = {
  "OPENING": "bg-blue-500",
  "GATEKEEPER": "bg-amber-500",
  "DECISION MAKER REACHED": "bg-emerald-500",
  "DISCOVERY": "bg-cyan-500",
  "VACANCY QUALIFICATION": "bg-indigo-500",
  "COMMERCIAL DISCUSSION": "bg-purple-500",
  "OBJECTION": "bg-red-500",
  "OBJECTION DISCOVERY": "bg-orange-500",
  "BUYING SIGNAL": "bg-green-500",
  "CLOSING": "bg-emerald-600",
  "FOLLOW UP": "bg-slate-500",
};

const CONFIDENCE_CONFIG = {
  high: { label: "HIGH CONFIDENCE", color: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-900/20", icon: CheckCircle },
  medium: { label: "MEDIUM CONFIDENCE", color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-900/20", icon: ShieldCheck },
  low: { label: "UNCERTAIN", color: "text-red-500", bg: "bg-red-50 dark:bg-red-900/20", icon: WarningCircle },
};

export default function MrBanksPanel({ callId, callStatus, transcript }) {
  const [stage, setStage] = useState("opening");
  const [recommendation, setRecommendation] = useState(null);
  const [brief, setBrief] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [history, setHistory] = useState([]);
  const lastAnalysed = useRef("");
  const transcribeTimer = useRef(null);

  // Load pre-call brief
  useEffect(() => {
    if (!callId) return;
    api.get(`/mr-banks/pre-call-brief/${callId}`).then(({ data }) => setBrief(data.brief)).catch(() => {});
  }, [callId]);

  // Poll live transcription during active call. Each poll drains only the
  // audio captured since the last drain (backend), so a shorter interval
  // shortens how far behind the live conversation Mr Banks runs.
  // inFlightRef guards against a slow Whisper round-trip overlapping with
  // the next tick, which previously could pile up polls and made responses
  // arrive further and further behind (feeling like it was replaying older
  // parts of the conversation instead of responding live).
  const inFlightRef = useRef(false);
  useEffect(() => {
    const isActive = callStatus === "connected" || callStatus === "answered";
    if (!isActive || !callId) {
      if (transcribeTimer.current) clearInterval(transcribeTimer.current);
      return;
    }
    const poll = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const { data } = await api.post(`/telephony/call/${callId}/transcribe`);
        if (data.transcript) {
          setLiveTranscript(prev => {
            const updated = prev ? prev + " " + data.transcript : data.transcript;
            return updated;
          });
        }
      } catch {}
      inFlightRef.current = false;
    };
    transcribeTimer.current = setInterval(poll, 3000);
    // First poll after 1.5s
    setTimeout(poll, 1500);
    return () => { if (transcribeTimer.current) clearInterval(transcribeTimer.current); };
  }, [callStatus, callId]);

  // Use live transcript or passed-in transcript
  const currentTranscript = liveTranscript || transcript || "";

  // Analyse transcript when it changes. The AI reads the last ~2000 characters so it
  // has the conversation context, not just the latest fragment. Only one analysis
  // runs at a time; if new speech arrives meanwhile, the newest text is analysed
  // next (older, superseded text is skipped so coaching never lags behind).
  const analysingRef = useRef(false);
  const pendingText = useRef("");
  const stagesSeen = useRef([]);
  const analyse = useCallback(async (text) => {
    if (!text || text === lastAnalysed.current || text.length < 10) return;
    if (analysingRef.current) { pendingText.current = text; return; }
    analysingRef.current = true;
    lastAnalysed.current = text;
    setAnalysing(true);
    try {
      const { data } = await api.post("/mr-banks/analyse", {
        transcript: text.slice(-2000), current_state: stage, call_id: callId,
      });
      if (data.recommendation?.stage) {
        setStage(data.stage);
        setRecommendation(data.recommendation);
        stagesSeen.current = [...new Set([...stagesSeen.current, data.stage])];
        setHistory(prev => [...prev.slice(-20), { stage: data.recommendation.stage, sub: data.recommendation.sub_type, time: new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }]);
      }
    } catch {}
    analysingRef.current = false;
    setAnalysing(false);
    if (pendingText.current && pendingText.current !== lastAnalysed.current) {
      const next = pendingText.current; pendingText.current = "";
      analyse(next);
    }
  }, [stage, callId]);

  useEffect(() => {
    if (currentTranscript) analyse(currentTranscript);
  }, [currentTranscript, analyse]);

  // When the call ends, save Mr Banks' analysis onto the call record.
  const [postCall, setPostCall] = useState(null);
  const lastCall = useRef({ id: null, active: false, transcript: "" });
  useEffect(() => {
    const active = callStatus === "connected" || callStatus === "answered";
    if (active && callId) {
      if (lastCall.current.id !== callId) { setPostCall(null); stagesSeen.current = []; }
      lastCall.current = { id: callId, active: true, transcript: liveTranscript };
    } else if (lastCall.current.active) {
      const { id, transcript: t } = lastCall.current;
      lastCall.current = { id, active: false, transcript: "" };
      if (id && t && t.length > 40) {
        api.post("/mr-banks/post-call-analysis", { call_id: id, transcript: t, stages: stagesSeen.current })
          .then(({ data }) => setPostCall(data)).catch(() => {});
      }
    }
  }, [callStatus, callId, liveTranscript]);

  // A new call starts with a clean slate
  useEffect(() => {
    if (!callId) return;
    setLiveTranscript(""); setRecommendation(null); setStage("opening"); setHistory([]);
    lastAnalysed.current = ""; pendingText.current = "";
  }, [callId]);

  const rec = recommendation;
  const stageLabel = rec?.stage || stage.replace(/_/g, " ").toUpperCase();
  const stageColor = STAGE_COLORS[stageLabel] || "bg-slate-500";
  const conf = CONFIDENCE_CONFIG[rec?.confidence || "low"];
  const ConfIcon = conf.icon;
  const isActive = callStatus === "connected" || callStatus === "answered";

  return (
    <div className="bg-white dark:bg-[#1E293B] rounded-lg border border-[#E5E7EB] dark:border-[#334155] overflow-hidden" data-testid="mr-banks-panel">
      {/* Header */}
      <div className="bg-[#0F172A] px-4 py-3 flex items-center gap-3">
        <Brain size={22} className="text-[#C5A059]" />
        <div>
          <div className="text-sm font-bold text-white tracking-wide">MR BANKS</div>
          <div className="text-[10px] text-[#94A3B8] uppercase tracking-widest">AI Call Coach{rec?.engine === "ai" ? ` · ${rec.model}${rec.latency_ms ? ` · ${(rec.latency_ms / 1000).toFixed(1)}s` : ""}` : rec?.engine === "rules" ? " · offline mode" : ""}</div>
        </div>
        <div className="flex-1" />
        {analysing && <div className="w-4 h-4 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />}
        {isActive && (
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] text-emerald-400 font-medium">LIVE</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Current Stage — 32-40px */}
        <div>
          <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Current Stage</div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${stageColor}`} />
            <span className="text-2xl md:text-[32px] font-black text-[#0F172A] dark:text-white leading-tight" data-testid="mr-banks-stage">
              {stageLabel}
            </span>
          </div>
          {rec?.sub_type && (
            <div className="text-base font-semibold text-[#C5A059] mt-1">{rec.sub_type}</div>
          )}
        </div>

        {rec?.requires_approval && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
            <WarningCircle size={18} className="text-red-600 flex-shrink-0" />
            <span className="text-sm font-bold text-red-700 dark:text-red-400">MANAGEMENT APPROVAL REQUIRED</span>
          </div>
        )}

        {/* Client Said */}
        {rec?.client_said && (
          <div>
            <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Client Just Said</div>
            <div className="bg-[#F9FAFB] dark:bg-[#0F172A] rounded-lg p-3 text-sm text-[#374151] dark:text-[#CBD5E1] italic leading-relaxed">
              "{rec.client_said}"
            </div>
          </div>
        )}

        {/* Objection detected */}
        {rec?.objection_detected && (
          <div className="bg-red-600 rounded-lg px-4 py-3" data-testid="mr-banks-objection">
            <div className="text-xs text-red-100 uppercase tracking-wider font-bold">Objection Detected</div>
            <div className="text-xl font-black text-white leading-tight">{rec.sub_type || "OBJECTION"}</div>
            {rec.objection_context && <div className="text-sm text-red-50 mt-0.5">{rec.objection_context}</div>}
          </div>
        )}

        {/* Why they are saying it */}
        {rec?.meaning && (
          <div>
            <div className="text-xs text-[#6B7280] uppercase tracking-wider font-bold mb-1">{rec?.objection_detected ? "Why They Are Saying It" : "What This Means"}</div>
            <div className="text-base font-semibold text-[#374151] dark:text-[#CBD5E1] leading-relaxed">{rec.meaning}</div>
          </div>
        )}

        {/* SAY THIS — Primary focus, 24-30px */}
        {rec?.say_this && (
          <div className="bg-[#0F172A] dark:bg-[#C5A059]/10 rounded-lg p-4 border-2 border-[#C5A059]" data-testid="mr-banks-say-this">
            <div className="text-xs text-[#C5A059] uppercase tracking-wider font-bold mb-2">Say This</div>
            <div className="text-2xl md:text-3xl font-bold text-white dark:text-[#F1F5F9] leading-snug">
              {rec.say_this}
            </div>
          </div>
        )}

        {/* ASK THIS */}
        {rec?.ask_this && (
          <div className="rounded-lg p-4 border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" data-testid="mr-banks-ask-this">
            <div className="text-xs text-emerald-700 uppercase tracking-wider font-bold mb-2">Ask This</div>
            <div className="text-xl md:text-2xl font-bold text-[#0F172A] dark:text-white leading-snug">{rec.ask_this}</div>
          </div>
        )}

        {/* Next move */}
        {rec?.next_move && rec.next_move !== "keep_talking" && (
          <div className="text-sm font-black uppercase tracking-wider text-[#C5A059]" data-testid="mr-banks-next-move">
            {{ stop_and_listen: "Stop selling — listen", secure_email_details: "Secure their email & details now", close: "Move to close", arrange_follow_up: "Arrange a follow-up" }[rec.next_move] || rec.next_move.replace(/_/g, " ")}
          </div>
        )}

        {/* Buying signals / requirements */}
        {(rec?.buying_signals?.length > 0 || rec?.client_requirements?.length > 0) && (
          <div className="flex flex-wrap gap-1.5">
            {rec.buying_signals?.map(b => <span key={"b" + b} className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">✓ {b}</span>)}
            {rec.client_requirements?.map(r => <span key={"r" + r} className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs">{r}</span>)}
          </div>
        )}

        {/* Objective */}
        {rec?.objective && (
          <div className="flex items-start gap-2">
            <Target size={16} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Objective</div>
              <div className="text-sm text-[#374151] dark:text-[#CBD5E1]">{rec.objective}</div>
            </div>
          </div>
        )}

        {/* Don't Say */}
        {rec?.dont_say && (
          <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-3 border border-red-200 dark:border-red-800">
            <div className="text-xs text-red-600 uppercase tracking-wider font-bold mb-1">Do Not Say This</div>
            <div className="text-base font-bold text-red-700 dark:text-red-400">{rec.dont_say}</div>
          </div>
        )}

        {/* Confidence */}
        {rec?.confidence && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${conf.bg}`}>
            <ConfIcon size={14} className={conf.color} />
            <span className={`text-[10px] font-bold ${conf.color} uppercase tracking-wider`}>{conf.label}</span>
          </div>
        )}

        {/* Next Paths */}
        {rec?.next_paths?.length > 0 && (
          <div>
            <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">Next Possible Paths</div>
            <div className="flex flex-wrap gap-1.5">
              {rec.next_paths.map(p => (
                <Badge key={p} variant="outline" className="text-[9px] capitalize cursor-pointer hover:bg-[#F3F4F6] dark:hover:bg-[#334155]" onClick={() => setStage(p)}>
                  {p.replace(/_/g, " ")}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Pre-call brief (when idle) */}
        {!rec && brief && (
          <div className="space-y-2">
            <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Pre-Call Brief</div>
            {brief.company && (
              <div className="text-sm"><span className="font-medium text-[#0F172A] dark:text-white">{brief.company.name}</span> {brief.company.industry && <span className="text-[#6B7280]">— {brief.company.industry}</span>}</div>
            )}
            {brief.contact && <div className="text-sm text-[#374151] dark:text-[#CBD5E1]">Contact: {brief.contact}</div>}
            {brief.vacancies?.length > 0 && (
              <div className="text-xs text-[#6B7280]">{brief.vacancies.length} vacancies: {brief.vacancies.map(v => v.title).join(", ")}</div>
            )}
            {brief.previous_calls?.length > 0 && (
              <div className="text-xs text-[#6B7280]">{brief.previous_calls.length} previous calls</div>
            )}
          </div>
        )}

        {/* No recommendation yet */}
        {!rec && !brief && (
          <div className="text-center py-6 text-sm text-[#9CA3AF]">
            <SpeakerHigh size={28} className="mx-auto mb-2 text-[#D1D5DB]" />
            {isActive ? "Listening to the conversation..." : "Mr Banks will activate when a call connects."}
          </div>
        )}

        {/* Live transcript indicator */}
        {isActive && liveTranscript && (
          <div className="mt-2 pt-2 border-t border-[#334155]">
            <div className="text-[9px] text-[#64748B] uppercase tracking-wider mb-1">Live Transcript</div>
            <div className="text-xs text-[#64748B] dark:text-[#94A3B8] max-h-[72px] overflow-y-auto leading-relaxed">{liveTranscript.slice(-400)}</div>
          </div>
        )}

        {/* Post-call summary (saved on the call record) */}
        {!isActive && postCall && (
          <div className="rounded-lg border border-[#C5A059] p-3 space-y-1" data-testid="mr-banks-post-call">
            <div className="text-xs font-bold uppercase tracking-wider text-[#C5A059]">Call Summary{postCall.average ? ` · ${postCall.average}/10` : ""}</div>
            {postCall.outcome && <div className="text-sm font-semibold text-[#0F172A] dark:text-white">{postCall.outcome}</div>}
            {postCall.summary && <div className="text-sm text-[#374151] dark:text-[#CBD5E1]">{postCall.summary}</div>}
            {postCall.follow_up && <div className="text-sm"><span className="font-semibold">Follow-up:</span> {postCall.follow_up}</div>}
            {postCall.coaching_tip && <div className="text-sm"><span className="font-semibold">Coaching tip:</span> {postCall.coaching_tip}</div>}
          </div>
        )}

        {/* Coaching event history */}
        {history.length > 0 && (
          <div className="mt-2 pt-2 border-t border-[#E5E7EB] dark:border-[#334155]">
            <div className="text-[9px] text-[#9CA3AF] uppercase tracking-wider mb-1">Session Events</div>
            <div className="max-h-[80px] overflow-y-auto space-y-0.5">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[9px] text-[#64748B]">
                  <span className="text-[#9CA3AF] min-w-[50px]">{h.time}</span>
                  <span className="font-medium text-[#374151] dark:text-[#CBD5E1]">{h.stage}</span>
                  {h.sub && <span className="text-[#C5A059]">{h.sub}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
